import React, { useState } from 'react';
import { getCurrentUser } from '@aws-amplify/auth';
import * as registrationService from '../../services/registration/registrationService';
import { useNavigate } from 'react-router-dom';

const RegistrationTestButton = ({ event }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] ${type.toUpperCase()}: ${message}`);
  };

  const testRegistrationFlow = async () => {
    if (!event || !event.id) {
      addLog('No event provided for testing', 'error');
      return;
    }

    setIsLoading(true);
    setLogs([]);
    
    try {
      // Step 1: Get current user
      addLog('Step 1: Getting current user...', 'info');
      const user = await getCurrentUser();
      if (!user || !user.userId) {
        addLog('Failed to get authenticated user', 'error');
        return;
      }
      
      const currentUserId = user.userId;
      setUserId(currentUserId);
      addLog(`Found user: ${currentUserId}`, 'success');

      // Step 2: Check for existing registration
      addLog('Step 2: Checking for existing registration...', 'info');
      const existingReg = await registrationService.checkExistingRegistration(event.id, currentUserId);
      
      if (existingReg) {
        addLog(`Found existing registration: ${existingReg.registrationId}`, 'warning');
        addLog('Navigating to existing registration...', 'info');
        navigate(`/user/registration/overview?eventId=${event.id}`);
        return;
      } else {
        addLog('No existing registration found', 'info');
      }

      // Step 3: Create new registration
      addLog('Step 3: Creating new registration...', 'info');
      const newRegistration = await registrationService.createNewRegistration(
        event.id, 
        currentUserId, 
        {
          name: event.name,
          startDate: event.startDate,
          endDate: event.endDate,
          location: event.location,
          venue: event.venue
        }
      );

      if (newRegistration && newRegistration.registrationId) {
        addLog(`Successfully created registration: ${newRegistration.registrationId}`, 'success');
        addLog('Navigating to registration flow...', 'info');
        navigate(`/user/registration/overview?eventId=${event.id}`);
      } else {
        addLog('Failed to create registration - no ID returned', 'error');
      }

    } catch (error) {
      addLog(`Error: ${error.message}`, 'error');
      console.error('Registration test error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative group">
      <button
        onClick={testRegistrationFlow}
        disabled={isLoading || !event?.id}
        className={`btn btn-xs text-xs ${
          isLoading || !event?.id
            ? 'btn-disabled'
            : 'bg-green-600 text-white hover:bg-green-700'
        }`}
        title="Debug: Test Registration Flow"
      >
        {isLoading ? '🔄' : '🧪'}
      </button>

      {/* Tooltip/Debug Panel */}
      {logs.length > 0 && (
        <div className="absolute top-full right-0 mt-1 p-2 bg-white border border-gray-300 rounded shadow-lg max-w-xs z-50 opacity-0 group-hover:opacity-100 transition-opacity">
          <h4 className="font-medium text-xs mb-1">Debug Logs:</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {logs.slice(-3).map((log, index) => (
              <div
                key={index}
                className={`text-xs p-1 rounded ${
                  log.type === 'error' ? 'bg-red-100 text-red-800' :
                  log.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                  log.type === 'success' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}
              >
                {log.message}
              </div>
            ))}
          </div>
          {userId && (
            <div className="mt-1 text-xs text-gray-500 border-t pt-1">
              User: {userId.substring(0, 8)}...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegistrationTestButton;
